import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { DownloadIcon, HamburgerIcon } from "@chakra-ui/icons";
import {
	Badge,
	Box,
	Flex,
	HStack,
	Icon,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	SimpleGrid,
	Td,
	Text,
	Tr,
	useColorModeValue,
	useDisclosure,
	useToast,
	VStack,
} from "@chakra-ui/react";
import moment from "moment/moment";
import { FiCalendar, FiDownload, FiFileText, FiInfo } from "react-icons/fi";

import {
	MODAL_GRID_SPACING,
	MODAL_SECTION_SPACING,
	ModalFooterActions,
	PropertyConfirmModal,
	PropertyModalSection,
	PropertyModalShell,
} from "../PropertyModal/shared";
import { removeFinancialStatementAsync, updateFinancialStatementActiveAsync } from "../../redux/user/userSlice";
import { API } from "../../utils/http";
import { fullDateTimeFormat } from "../../utils/types";

function FinancialStatementRow(props) {
	const { date, active, year, type, description, name, documents, property, id } = props;
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { isOpen: isDeleteConfirmationOpen, onOpen: onDeleteConfirmationOpen, onClose: onDeleteConfirmationClose } = useDisclosure();
	const toast = useToast();
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const iconColor = useColorModeValue("blue.500", "blue.300");

	const markAsActive = (financialStatementId) => {
		const payload = { financialStatementId, propertyId: property.id, active: true };
		console.log("mark financial statement as active: ", payload);
		dispatch(updateFinancialStatementActiveAsync(payload));
	};
	const markAsInactive = (financialStatementId) => {
		const payload = { financialStatementId, propertyId: property.id, active: false };
		console.log("mark financial statement as active: ", payload);
		dispatch(updateFinancialStatementActiveAsync(payload));
	};
	const handleGetDocument = (documentId, documentName) => {
		const payload = { propertyId: property.id, documentId, financialStatementId: id, documentName };
		console.log(payload);
		API.put("get-financial-statement-document", payload)
			.then((r) => {
				console.log(r);
				window.open(r.data.url, "_blank");
			})
			.catch((e) => {
				console.log(e);
				toast({
					title: "Error encountered",
					description: e.response.data.error.message,
					status: "error",
					duration: 9000,
					isClosable: true,
				});
			});
	};
	const handleDeletePayment = () => {
		const payload = { propertyId: property.id, financialStatementId: id };
		console.log(payload);
		onDeleteConfirmationClose();
		dispatch(removeFinancialStatementAsync(payload));
	};

	const renderDeleteConfirmationModal = () => (
		<PropertyConfirmModal
			isOpen={isDeleteConfirmationOpen}
			onClose={onDeleteConfirmationClose}
			onConfirm={handleDeletePayment}
			message={t("property.deleteFinancialStatementConfirmation")}
			emphasis={t("property.actionCannotBeUndone")}
		/>
	);

	const renderViewDocumentsModal = () => (
		<PropertyModalShell
			isOpen={isOpen}
			onClose={onClose}
			size="4xl"
			icon={FiFileText}
			title={t("property.financialStatementDetailsTitle")}
			subtitle={t("property.viewFinancialStatementSubtitle")}
			footer={<ModalFooterActions onCancel={onClose} cancelLabel={t("misc.close")} />}
		>
			<SimpleGrid columns={{ base: 1, lg: 2 }} spacing={MODAL_GRID_SPACING}>
				<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
					<PropertyModalSection
						icon={FiInfo}
						title={t("property.statementSummary")}
						headerExtra={
							<Badge colorScheme={active ? "green" : "gray"} fontSize="xs" px={2} py={0.5} borderRadius="full">
								{active ? t("property.activeLabel") : t("property.inactiveLabel")}
							</Badge>
						}
					>
						<VStack spacing={3} align="stretch">
							<HStack alignItems="center">
								<HStack spacing={2} alignItems="center">
									<Icon as={FiFileText} color={iconColor} boxSize={4} />
									<Text fontSize="sm" fontWeight="medium">
										{t("table.name")}:
									</Text>
								</HStack>
								<Text fontSize="sm" fontWeight="bold">
									{name}
								</Text>
							</HStack>
							<SimpleGrid columns={2} spacing={4}>
								<HStack alignItems="center">
									<HStack spacing={2} alignItems="center">
										<Icon as={FiCalendar} color={iconColor} boxSize={4} />
										<Text fontSize="sm" fontWeight="medium">
											{t("table.year")}:
										</Text>
									</HStack>
									<Text fontSize="sm" fontWeight="medium">
										{year}
									</Text>
								</HStack>
								<HStack alignItems="center">
									<HStack spacing={2} alignItems="center">
										<Icon as={FiFileText} color={iconColor} boxSize={4} />
										<Text fontSize="sm" fontWeight="medium">
											{t("table.type")}:
										</Text>
									</HStack>
									<Badge colorScheme="blue" fontSize="sm" px={3} py={1} borderRadius="full">
										{t(`table.${type}`)}
									</Badge>
								</HStack>
							</SimpleGrid>
							<HStack alignItems="center">
								<HStack spacing={2} alignItems="center">
									<Icon as={FiCalendar} color={iconColor} boxSize={4} />
									<Text fontSize="sm" fontWeight="medium">
										{t("table.addedDate")}:
									</Text>
								</HStack>
								<Text fontSize="sm" fontWeight="medium">
									{moment(date).format(fullDateTimeFormat)}
								</Text>
							</HStack>
						</VStack>
					</PropertyModalSection>

					{description && (
						<PropertyModalSection icon={FiInfo} title={t("property.descriptionLabel")}>
							<Text fontSize="sm" color="gray.700" bg="gray.50" p={3} borderRadius="md">
								{description}
							</Text>
						</PropertyModalSection>
					)}
				</VStack>

				<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
					<PropertyModalSection
						icon={FiDownload}
						title={t("property.documentsLabel")}
						headerExtra={
							<Badge colorScheme="blue" fontSize="xs">
								{documents?.length || 0}
							</Badge>
						}
					>
						{documents && documents.length > 0 ? (
							<VStack spacing={3} align="stretch">
								{documents.map((d, index) => (
									<Box
										key={index}
										p={3}
										border="1px"
										borderColor="gray.200"
										borderRadius="md"
										_hover={{ bg: "gray.50" }}
										cursor="pointer"
										onClick={() => handleGetDocument(d.id, d.documentName)}
									>
										<HStack justify="space-between">
											<HStack spacing={3}>
												<Icon as={FiFileText} color={iconColor} boxSize={4} />
												<VStack align="start" spacing={0}>
													<Text fontSize="sm" fontWeight="medium" noOfLines={1}>
														{d.documentName}
													</Text>
													<Text fontSize="xs" color="gray.500">
														{t("property.clickToDownload")}
													</Text>
												</VStack>
											</HStack>
											<Icon as={DownloadIcon} color={iconColor} boxSize={4} />
										</HStack>
									</Box>
								))}
							</VStack>
						) : (
							<Box textAlign="center" py={6}>
								<Icon as={FiFileText} color="gray.300" boxSize={8} mb={3} />
								<Text fontSize="sm" color="gray.500">
									{t("property.noDocumentsAttached")}
								</Text>
							</Box>
						)}
					</PropertyModalSection>
				</VStack>
			</SimpleGrid>
		</PropertyModalShell>
	);

	return (
		<Tr>
			<Td minWidth={{ sm: "150px" }} pl="0px">
				<Flex align="center" minWidth="100%" flexWrap="nowrap">
					<Flex direction="column">
						<Text fontSize="md" minWidth="100%">
							{moment(date).format(fullDateTimeFormat)}
						</Text>
						<Badge colorScheme="green" width={"fit-content"} hidden={!active}>
							Active
						</Badge>
					</Flex>
				</Flex>
			</Td>
			<Td>
				<Text fontSize="md" pb=".5rem">
					{name}
				</Text>
			</Td>
			<Td>
				<Text fontSize="md" pb=".5rem">
					{year}
				</Text>
			</Td>
			<Td>
				<Text fontSize="md" pb=".5rem">
					{t(`table.${type}`)}
				</Text>
			</Td>
			<Td>
				<Text fontSize="md" pb=".5rem">
					{description}
				</Text>
			</Td>

			<Td>
				<Menu closeOnSelect={false}>
					<MenuButton>
						<Icon as={HamburgerIcon} />
					</MenuButton>
					<MenuList>
						{active ? (
							<MenuItem
								onClick={() => {
									markAsInactive(id);
								}}
							>
								Mark as inactive
							</MenuItem>
						) : (
							<MenuItem
								onClick={() => {
									markAsActive(id);
								}}
							>
								Mark as Active
							</MenuItem>
						)}
						<MenuItem onClick={onOpen}>View Documents</MenuItem>
						<MenuItem onClick={onDeleteConfirmationOpen}>Delete</MenuItem>
					</MenuList>
				</Menu>
				{renderViewDocumentsModal()}
				{renderDeleteConfirmationModal()}
			</Td>
		</Tr>
	);
}

export default FinancialStatementRow;
